import { formatIsoDate, parseIsoDate, toDateOnlyIso } from "@/lib/date";

const ISO_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;
const MAX_RECURRING_LOOKAHEAD_DAYS = 14 * 7;

export type PickupRequestSchedule = {
  pickup_date: string;
  end_date?: string | null;
  days_of_week?: number[] | null;
  picker_name: string;
};

type DatedPickupRequest = PickupRequestSchedule & {
  id: number;
  is_valid?: boolean;
};

export type PickupOccurrenceGroup<T extends DatedPickupRequest> = {
  occurrenceDate: string;
  requests: T[];
};

/**
 * True when the request is a weekly series rather than a single date.
 */
export function isRecurringPickupRequest(
  request: Pick<PickupRequestSchedule, "days_of_week">,
): boolean {
  return Array.isArray(request.days_of_week) && request.days_of_week.length > 0;
}

/**
 * Localized weekday labels for ISO weekdays 1–7, in calendar order.
 */
export function formatPickupWeekdays(
  daysOfWeek: number[] | null | undefined,
  weekdayLabel: (day: number) => string,
): string {
  const days = [...new Set(daysOfWeek ?? [])]
    .filter((day) => day >= 1 && day <= 7)
    .sort((a, b) => a - b);

  return days.map((day) => weekdayLabel(day)).join(", ");
}

/** ISO weekday 1–7 (Monday–Sunday) for a local calendar date. */
function isoWeekdayFromIsoDate(isoDate: string): number | null {
  const date = parseIsoDate(isoDate);

  if (!date) {
    return null;
  }

  const weekday = date.getDay();
  return weekday === 0 ? 7 : weekday;
}

function addDaysIso(isoDate: string, days: number): string | null {
  const date = parseIsoDate(isoDate);

  if (!date) {
    return null;
  }

  date.setDate(date.getDate() + days);
  return formatIsoDate(date);
}

/**
 * Next calendar day this request applies, on or after today.
 * Recurring series without an end date are scanned for 14 weeks.
 */
export function getNextPickupOccurrence(
  request: PickupRequestSchedule,
  todayIso: string,
): string | null {
  const start = toDateOnlyIso(request.pickup_date);
  const today = toDateOnlyIso(todayIso);

  if (!start || !today) {
    return null;
  }

  if (!isRecurringPickupRequest(request)) {
    const end = request.end_date ? toDateOnlyIso(request.end_date) : null;
    const everyDay =
      request.end_date === null || (end != null && end.length > 0 && end !== start);

    if (!everyDay) {
      return start >= today ? start : null;
    }

    const from = start > today ? start : today;
    const last =
      end && end.length > 0 ? end : addDaysIso(from, MAX_RECURRING_LOOKAHEAD_DAYS);

    if (!last || from > last) {
      return null;
    }

    return from;
  }

  const weekdays = new Set(
    (request.days_of_week ?? []).filter((day) => day >= 1 && day <= 7),
  );

  if (weekdays.size === 0) {
    return null;
  }

  const from = start > today ? start : today;
  const end = request.end_date ? toDateOnlyIso(request.end_date) : null;
  const last =
    end && end.length > 0
      ? end
      : addDaysIso(from, MAX_RECURRING_LOOKAHEAD_DAYS);

  if (!last || from > last) {
    return null;
  }

  let cursor: string | null = from;

  while (cursor && cursor <= last) {
    const weekday = isoWeekdayFromIsoDate(cursor);

    if (weekday != null && weekdays.has(weekday)) {
      return cursor;
    }

    cursor = addDaysIso(cursor, 1);
  }

  return null;
}

export type PickupOccurrence<T extends DatedPickupRequest> = {
    request: T;
    occurrenceDate: string;
};

/**
 * Upcoming occurrences for valid requests, soonest first.
 */
export function getUpcomingPickupOccurrences<T extends DatedPickupRequest>(
    requests: T[],
    todayIso: string,
): PickupOccurrence<T>[] {
    const dated: PickupOccurrence<T>[] = [];

    for (const request of requests) {
        if (request.is_valid === false) {
            continue;
        }

        const occurrenceDate = getNextPickupOccurrence(request, todayIso);

        if (occurrenceDate) {
            dated.push({ request, occurrenceDate });
        }
    }

    dated.sort(
        (left, right) =>
            left.occurrenceDate.localeCompare(right.occurrenceDate) ||
            left.request.id - right.request.id,
    );

    return dated;
}

/**
 * Valid requests that share the soonest upcoming occurrence date.
 */
export function getPickupsOnNearestDate<T extends DatedPickupRequest>(
    requests: T[],
    todayIso: string,
): PickupOccurrenceGroup<T> | null {
    const dated = getUpcomingPickupOccurrences(requests, todayIso);

    if (dated.length === 0) {
        return null;
    }

    const occurrenceDate = dated[0].occurrenceDate;

    return {
        occurrenceDate,
        requests: dated
            .filter((item) => item.occurrenceDate === occurrenceDate)
            .map((item) => item.request),
    };
}

export { ISO_WEEKDAYS };
